import { toBoolean } from '@mnd-frontend/ts';
import PricingCard from '@mnd-frontend/ui/public/components/CardPricing';
import type { CardPricingProps } from '@mnd-frontend/ui/public/components/CardPricing/index.type';
import styled from 'styled-components';
import type { PricingPlansFragment } from '../../../../wp-generated/types';

const Container = styled.section`
  background: #F7F7F9;
  padding: 5rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;

const Cell = styled.div`
  display: grid;
  justify-content: center;
  grid-template-columns: 1fr;
`;

const Plans = styled.div<{ $numberOfPlans: number; $breakpointForSingleColumn: number }>`
  display: grid;
  gap: 2rem;
  justify-content: center;
  grid-template-columns: repeat(${props => props.$numberOfPlans}, minmax(22.5rem, 28.75rem));
  @media screen and (max-width: ${props => props.$breakpointForSingleColumn}px) {
    grid-template-columns: minmax(22.5rem, 28.75rem);
  }
`;

const Heading = styled.h2`
  color: #181646;
  text-align: center;

  font-size: 3rem;
  font-weight: 500;
  line-height: 1;
  letter-spacing: -2.4px;
`;

type HeadingLevels = 'h2' | 'h3';

const PricingPlans = ({ moduleData }: { moduleData: PricingPlansFragment }) => {
  const headingLevel = moduleData.headingLevel?.[0] as HeadingLevels;
  const pricingCardHeadingLevel = headingLevel === 'h2' ? 'h3' : 'h4';

  const plans = moduleData.pricingPlans?.filter(toBoolean) ?? [];
  const breakpointForSingleColumn = plans.length * 392;

  if (!moduleData.title) return null;

  return (
    <Container>
      <Heading as={headingLevel}>{moduleData.title}</Heading>
      <Plans $numberOfPlans={plans.length} $breakpointForSingleColumn={breakpointForSingleColumn}>
        {plans.map((plan, i) => {
          if (
            !plan.features?.length ||
            !plan.price?.amount ||
            !plan.link ||
            !plan.title ||
            !plan.description
          )
            return null;

          return (
            <Cell key={i}>
              <PricingCard
                breakpointForSingleColumn={breakpointForSingleColumn}
                featured={!!plan.featured}
                descriptionHtml={plan.description}
                cta={{ text: plan.link?.title || '', url: plan.link?.url ?? '' }}
                featuredPillText={plan.featuredPillText}
                titleRow2={plan.titleRow2 ?? ''}
                title={plan.title ?? ''}
                price={plan.price as typeof plan.price & { amount: string }}
                headingLevel={pricingCardHeadingLevel}
                features={plan.features.filter(toBoolean).map(featureGroup => ({
                  heading: featureGroup.heading ?? '',
                  icon: featureGroup.icon?.[0] as CardPricingProps['features'][number]['icon'],
                  usps:
                    featureGroup.usps
                      ?.filter(toBoolean)
                      .map(({ usp }) => usp)
                      .filter(toBoolean) ?? [],
                }))}
              />
            </Cell>
          );
        })}
      </Plans>
    </Container>
  );
};

export default PricingPlans;
